import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { PreferencesService } from "../services/preferencesService";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date string according to user preferences
 * @param dateString - The date string to format (ISO format or parseable date string)
 * @param options - Optional formatting options
 * @returns Formatted date string or fallback value
 */
export function formatDate(
  dateString: string | undefined | null,
  options: { fallback?: string; includeTime?: boolean } = {}
): string {
  const { fallback = '-', includeTime = false } = options;
  
  if (!dateString) return fallback;
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallback;
    
    const preferences = PreferencesService.getPreferences();
    const format = preferences.dateFormat;
    const separator = preferences.dateSeparator || '/';
    
    const day = date.getDate();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const year = date.getFullYear();
    
    // Pad with zeros
    const dd = day.toString().padStart(2, '0');
    const mm = month.toString().padStart(2, '0');
    const yyyy = year.toString();
    
    let formattedDate: string;
    
    // Match the format pattern (case-insensitive)
    const formatUpper = format.toUpperCase();
    
    if (formatUpper.includes('YYYY') && formatUpper.indexOf('YYYY') < formatUpper.indexOf('MM')) {
      // YYYY-MM-DD or YYYY/MM/DD
      formattedDate = `${yyyy}${separator}${mm}${separator}${dd}`;
    } else if (formatUpper.indexOf('DD') < formatUpper.indexOf('MM')) {
      // DD-MM-YYYY or DD/MM/YYYY
      formattedDate = `${dd}${separator}${mm}${separator}${yyyy}`;
    } else {
      // MM-DD-YYYY or MM/DD/YYYY (default)
      formattedDate = `${mm}${separator}${dd}${separator}${yyyy}`;
    }
    
    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      formattedDate += ` ${hours}:${minutes}`;
    }
    
    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return fallback;
  }
}

/**
 * Format a date for reporting analyses (month, quarter, year, etc.)
 * @param date - The date object
 * @param granularity - The time granularity (month, quarter, year, week)
 * @returns Formatted string
 */
export function formatDateForAnalysis(date: Date, granularity: string): string {
  const preferences = PreferencesService.getPreferences();
  const format = preferences.dateFormat;
  const separator = preferences.dateSeparator || '/';
  
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const quarter = Math.floor((date.getMonth()) / 3) + 1;
  
  // Pad with zeros
  const mm = month.toString().padStart(2, '0');
  const yyyy = year.toString();
  
  // Determine order from user's date format
  const formatUpper = format.toUpperCase();
  const yearFirst = formatUpper.includes('YYYY') && formatUpper.indexOf('YYYY') < formatUpper.indexOf('MM');
  
  switch (granularity) {
    case 'year':
      return yyyy;
      
    case 'quarter':
      if (yearFirst) {
        return `${yyyy}${separator}Q${quarter}`;
      } else {
        return `Q${quarter}${separator}${yyyy}`;
      }
      
    case 'month':
      if (yearFirst) {
        return `${yyyy}${separator}${mm}`;
      } else {
        return `${mm}${separator}${yyyy}`;
      }
      
    case 'week':
      // For week, calculate week number
      const startOfYear = new Date(date.getFullYear(), 0, 1);
      const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
      const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
      const ww = weekNum.toString().padStart(2, '0');
      
      if (yearFirst) {
        return `${yyyy}${separator}W${ww}`;
      } else {
        return `W${ww}${separator}${yyyy}`;
      }
      
    case 'date':
    default:
      return formatDate(date.toISOString(), {});
  }
}

/**
 * Parse a formatted date string back to a Date object for sorting
 * @param formattedDate - The formatted date string (e.g., "02/2025", "2025-Q1", "W07/2025")
 * @returns Date object for comparison, or null if unparseable
 */
export function parseFormattedDateForSorting(formattedDate: string): Date | null {
  if (!formattedDate || formattedDate === 'Unknown' || formattedDate === 'No Data') {
    return null;
  }

  const preferences = PreferencesService.getPreferences();
  const format = preferences.dateFormat;
  const formatUpper = format.toUpperCase();
  const yearFirst = formatUpper.includes('YYYY') && formatUpper.indexOf('YYYY') < formatUpper.indexOf('MM');

  // Handle quarters (e.g., "2025-Q1" or "Q1/2025")
  if (formattedDate.includes('Q')) {
    const match = formattedDate.match(/Q(\d)/);
    const yearMatch = formattedDate.match(/(\d{4})/);
    if (match && yearMatch) {
      const quarter = parseInt(match[1]);
      const year = parseInt(yearMatch[1]);
      const month = (quarter - 1) * 3; // Start month of quarter (0-indexed)
      return new Date(year, month, 1);
    }
  }

  // Handle weeks (e.g., "2025-W07" or "W07/2025")
  if (formattedDate.includes('W')) {
    const weekMatch = formattedDate.match(/W(\d+)/);
    const yearMatch = formattedDate.match(/(\d{4})/);
    if (weekMatch && yearMatch) {
      const week = parseInt(weekMatch[1]);
      const year = parseInt(yearMatch[1]);
      // Approximate: use week number * 7 as day of year
      const dayOfYear = week * 7;
      const date = new Date(year, 0, 1);
      date.setDate(dayOfYear);
      return date;
    }
  }

  // Handle year only (e.g., "2025")
  if (/^\d{4}$/.test(formattedDate)) {
    return new Date(parseInt(formattedDate), 0, 1);
  }

  // Handle month formats (e.g., "02/2025", "2025-02", "2025/02")
  // Remove any separators and extract numbers
  const parts = formattedDate.split(/[/-]/).filter(p => p && /^\d+$/.test(p));
  
  if (parts.length === 2) {
    let year: number;
    let month: number;

    if (yearFirst) {
      // Format is YYYY-MM or YYYY/MM
      year = parseInt(parts[0]);
      month = parseInt(parts[1]) - 1; // JS months are 0-indexed
    } else {
      // Format is MM-YYYY or MM/YYYY
      month = parseInt(parts[0]) - 1;
      year = parseInt(parts[1]);
    }

    if (year >= 1900 && year <= 3000 && month >= 0 && month <= 11) {
      return new Date(year, month, 1);
    }
  }

  // If all else fails, try standard date parsing
  const attemptParse = new Date(formattedDate);
  if (!isNaN(attemptParse.getTime())) {
    return attemptParse;
  }

  return null;
}

/**
 * Compare two formatted date strings for sorting (oldest to newest)
 * @param a - First formatted date string
 * @param b - Second formatted date string
 * @returns Negative if a < b, positive if a > b, 0 if equal
 */
export function compareFormattedDates(a: string, b: string): number {
  const dateA = parseFormattedDateForSorting(a);
  const dateB = parseFormattedDateForSorting(b);

  // Handle null cases
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1; // Put unparseable dates at the end
  if (!dateB) return -1;

  return dateA.getTime() - dateB.getTime();
}

